// ─── SelectionFrame ────────────────────────────────────────────────────────
// Marco de selección reutilizable para clips dentro del preview del editor.
// Renderiza:
//   • un borde amarillo (brand-primary) alrededor del bounding box de su
//     contenedor (`inset: 0` — el caller posiciona el wrapper),
//   • cuatro corner-dots circulares + cuatro píldoras en cada lado, a modo
//     de manijas de resize,
//   • cada manija captura pointer events y emite callbacks con el delta
//     en pixels desde donde arrancó el drag, junto con el id del handle.
//
// Es agnóstico de la geometría real del clip: el caller decide qué
// significa "resize" (cambiar `widthPct` de un texto, `animScale` de una
// animación, etc.) y aplica la matemática correspondiente. SelectionFrame
// solo se ocupa de la UI y de propagar gestos.

import React, { useRef } from 'react';

export type SelectionHandle =
  | 'n'
  | 's'
  | 'e'
  | 'w'
  | 'ne'
  | 'nw'
  | 'se'
  | 'sw';

export type SelectionFramePhase = 'start' | 'move' | 'end';

export type SelectionFrameProps = {
  /** Cuáles manijas mostrar. Default: las 8 (4 esquinas + 4 lados).
   *  Para texto típicamente se omiten 'n' y 's' porque ahí no aporta:
   *  el alto se ajusta automáticamente al contenido. */
  handles?: SelectionHandle[];
  /** Callback unificado para drag de cualquier manija. dx/dy son deltas
   *  en pixels respecto al pointerdown. `phase` permite distinguir el
   *  inicio (commitar valor base), el move continuo y el final (típico
   *  para hacer un único `setClips` definitivo). */
  onResize?: (
    handle: SelectionHandle,
    dx: number,
    dy: number,
    phase: SelectionFramePhase,
  ) => void;
  /** Color del marco. Default: amarillo brand-primary. */
  color?: string;
  /** Mostrar solo el borde sin manijas (para clips no editables). */
  outlineOnly?: boolean;
};

const ALL_HANDLES: SelectionHandle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

const CURSOR: Record<SelectionHandle, string> = {
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
  nw: 'nwse-resize',
  se: 'nwse-resize',
};

// Posiciones de cada handle como % sobre el bounding box. El wrapper
// del handle se centra en este punto (`translate(-50%,-50%)`).
const POS: Record<SelectionHandle, { left: string; top: string }> = {
  nw: { left: '0%', top: '0%' },
  n: { left: '50%', top: '0%' },
  ne: { left: '100%', top: '0%' },
  e: { left: '100%', top: '50%' },
  se: { left: '100%', top: '100%' },
  s: { left: '50%', top: '100%' },
  sw: { left: '0%', top: '100%' },
  w: { left: '0%', top: '50%' },
};

const isCorner = (h: SelectionHandle): boolean => h.length === 2;

export const SelectionFrame: React.FC<SelectionFrameProps> = ({
  handles = ALL_HANDLES,
  onResize,
  color = '#FACC15', // amarillo brand-primary (~tailwind yellow-400)
  outlineOnly = false,
}) => {
  // Estado del drag actual. Vive en ref para no causar re-renders en cada
  // pointermove — el caller maneja su propio state vía onResize.
  const dragRef = useRef<{
    handle: SelectionHandle;
    pointerId: number;
    startX: number;
    startY: number;
  } | null>(null);

  const onDown = (handle: SelectionHandle) => (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    dragRef.current = {
      handle,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
    };
    try {
      (e.currentTarget as Element).setPointerCapture(e.pointerId);
    } catch {}
    onResize?.(handle, 0, 0, 'start');
  };

  const onMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    onResize?.(d.handle, e.clientX - d.startX, e.clientY - d.startY, 'move');
  };

  const onUp = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    onResize?.(d.handle, e.clientX - d.startX, e.clientY - d.startY, 'end');
    try {
      (e.currentTarget as Element).releasePointerCapture(e.pointerId);
    } catch {}
    dragRef.current = null;
  };

  return (
    <div
      // Borde alrededor del contenedor padre. `pointer-events-none` aquí
      // para que el contenedor (drag) siga recibiendo eventos cuando el
      // puntero pasa por encima de la línea del borde — solo las manijas
      // re-activan eventos.
      className="absolute inset-0 pointer-events-none"
      style={{
        outline: `1.5px solid ${color}`,
        outlineOffset: 0,
        // Sin background ni border para que el contenido interno se
        // siga viendo nítido — solo el outline.
      }}
    >
      {!outlineOnly &&
        handles.map(h => {
          const corner = isCorner(h);
          // Tamaños:
          //   • esquinas → círculo 11x11
          //   • lados N/S → píldora horizontal 20x7
          //   • lados E/W → píldora vertical 7x20
          const w = corner ? 11 : h === 'n' || h === 's' ? 20 : 7;
          const ht = corner ? 11 : h === 'n' || h === 's' ? 7 : 20;
          const pos = POS[h];
          return (
            <div
              key={h}
              onPointerDown={onDown(h)}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerCancel={onUp}
              className="absolute pointer-events-auto"
              style={{
                left: pos.left,
                top: pos.top,
                width: w,
                height: ht,
                transform: 'translate(-50%, -50%)',
                background: color,
                border: `1.5px solid ${color}`,
                borderRadius: corner ? '50%' : 999,
                cursor: CURSOR[h],
                touchAction: 'none',
                // Sombra sutil para que el handle se despegue del
                // contenido del clip (texto / imagen) cuando el fondo
                // tiene un color similar al amarillo.
                boxShadow: '0 1px 2px rgba(0,0,0,0.18)',
              }}
            />
          );
        })}
    </div>
  );
};
